// product-reviews.slice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { ProductReviewsParam } from "features/stock-ordering/core/stock-ordering.params";
import {
  GetProductReviewsRepository,
  ProductReviewsResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum ProductReviewsState {
  initial,
  inProgress,
  success,
  fail,
}

export interface ProductReview {
  id: string;
  rating: number;
  review: string;
  user_id: string;
  order_id: string;
  created_at: string;
  updated_at: string;
}

export interface ProductReviewsData {
  product_id: string;
  product_name: string;
  total_reviews: number;
  average_rating: number;
  rating_breakdown: {
    "5": number;
    "4": number;
    "3": number;
    "2": number;
    "1": number;
  };
  reviews: ProductReview[];
}

// Properly type the initial state interface
export interface ProductReviewsInitialState {
  status: ProductReviewsState;
  message: string;
  data: ProductReviewsData | undefined;
}

const initialState: ProductReviewsInitialState = {
  status: ProductReviewsState.initial,
  message: "",
  data: undefined,
};

export const getProductReviews = createAsyncThunk(
  "getProductReviews",
  async (productId: string, { rejectWithValue }) => {
    try {
      const param: ProductReviewsParam = { productId };
      const response: ProductReviewsResponse =
        await GetProductReviewsRepository(param);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

/* Main Slice */
export const productReviewsSlice = createSlice({
  name: "productReviews",
  initialState,
  reducers: {
    resetProductReviews: (state) => {
      state.status = ProductReviewsState.initial;
      state.message = "Reset request";
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProductReviews.pending, (state) => {
        state.status = ProductReviewsState.inProgress;
      })
      .addCase(getProductReviews.fulfilled, (state, action) => {
        if (action.payload) {
          state.status = ProductReviewsState.success;
          state.data = action.payload;
          state.message = "Reviews loaded successfully";
        }
      })
      .addCase(getProductReviews.rejected, (state, action) => {
        state.status = ProductReviewsState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

// Properly type the selector
export const selectProductReviews = (state: RootState) => state.productReviews;

export const { resetProductReviews } = productReviewsSlice.actions;
export default productReviewsSlice.reducer;
